import { useMemo } from 'react'
import { useNavigate, useSearchParams, Navigate, useParams } from 'react-router-dom'
import HomeContent from '@src/components/SectionContent'
import { ArrowLeft } from 'lucide-react'
import RiskProfileHazardSelector from '../components/RiskProfileHazardSelector'
import { SelectedElementStatsGrid } from '../components/SelectedElementStatsGrid'
import { SelectedElementInfoCard } from '../components/SelectedElementInfoCard'
import { SelectedElementTable } from '../components/SelectedElementTable'
import { useSelectedElementProfileQuery } from '../hooks/useProfileSelectedRequest'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'
import { useHazardGroupBuckets } from '../hooks/useHazardGroupBuckets'
import { useSelectedWorkspaceFromUrl } from '../hooks/useSelectedWorkspaceFromUrl'
import { useRiskFilters } from '../../shared/hooks/useRiskFilters'

const SelectedElementAnalysisPage = () => {
  const navigate = useNavigate()
  const { assetType, aggregateId } = useParams<{ assetType: string; aggregateId: string }>()
  const [searchParams] = useSearchParams()
  const selectedWorkspaceId = searchParams.get('workspace') ?? null
  const {
    selectedWorkspace,
    isLoading: isWorkspaceLoading,
    isMissingWorkspaceParam,
  } = useSelectedWorkspaceFromUrl()
  const { hazardGroups: selectedHazardGroups, setFilters } = useRiskFilters()
  const hazards = selectedWorkspace?.layers?.scenarios ?? []

  const hazardGroupBuckets = useHazardGroupBuckets(selectedWorkspace?.layers.scenarios ?? [])
  const hazardGroupNames = useMemo(
    () => hazardGroupBuckets.map((bucket) => Object.keys(bucket)[0]),
    [hazardGroupBuckets],
  )

  const handleHazardGroupsChange = (groups: string[]) => setFilters({ hazardGroups: groups })

  const {
    data: selectedElementProfileData,
    isLoading,
    isError,
  } = useSelectedElementProfileQuery({
    asset_type: assetType,
    aggregate_id: aggregateId,
    hazard_group_filter: selectedHazardGroups,
    hazards,
  })

  const category = useMemo(() => {
    const group = selectedElementProfileData?.[0]?.metadata_information?.group
    if (typeof group !== 'string') return ''

    try {
      const parsed = JSON.parse(group)
      return parsed && typeof parsed === 'object' ? Object.keys(parsed).join(', ') : ''
    } catch {
      return ''
    }
  }, [selectedElementProfileData])

  const type = useMemo(() => {
    const value = selectedElementProfileData?.[0]?.metadata_information?.type
    return typeof value === 'string' ? value.replace('_', ' ') : ''
  }, [selectedElementProfileData])

  if (!isWorkspaceLoading && (isMissingWorkspaceParam || !selectedWorkspace)) {
    return <Navigate to="/app/risk/profile" replace />
  }

  if (isLoading || isWorkspaceLoading) {
    return (
      <div className="bg-sidebar-accent min-h-[100%]">
        <div className="flex justify-center items-center h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="bg-sidebar-accent min-h-[100%]">
        <HomeContent className="mt-8">
          <div className="flex flex-col gap-4">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Error</h1>
            <p>There was an error fetching the selected element profile. Please try again later.</p>
          </div>
        </HomeContent>
      </div>
    )
  }

  if (!selectedElementProfileData || selectedElementProfileData.length === 0) {
    return (
      <div className="bg-sidebar-accent min-h-[100%]">
        <HomeContent className="mt-8">
          <div className="flex flex-col gap-4">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Error</h1>
            <p>No hazard scenarios for this workspace/ element combination were found.</p>
          </div>
        </HomeContent>
      </div>
    )
  }

  return (
    <div className="bg-sidebar-accent min-h-[100%]">
      <HomeContent className="mt-8">
        {/* Header */}
        <header className="mb-2">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/app/risk/profile/analysis?workspace=${selectedWorkspaceId}&metric=damage_cost`,
              )
            }
            className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:opacity-60 text-rexBlue"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>

          <div className="flex gap-4 justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
                {String(selectedElementProfileData[0].asset_information?.Name ?? '')}
              </h1>
              <p>
                <span>{category}</span> • <span className="capitalize">{type}</span>
              </p>
            </div>
            <div className="max-w-[300px] w-full">
              <RiskProfileHazardSelector
                label="Hazards"
                placeholder="All hazards"
                options={hazardGroupNames}
                selected={selectedHazardGroups}
                onChange={handleHazardGroupsChange}
              />
            </div>
          </div>
        </header>

        {/* Main  */}
        <div className="flex gap-4">
          <div className="w-2/3 flex flex-col gap-4">
            <SelectedElementStatsGrid selectedProfileData={selectedElementProfileData} />
            <SelectedElementTable exposures={selectedElementProfileData} />
          </div>
          <SelectedElementInfoCard
            information={selectedElementProfileData[0].asset_information}
            metadata={selectedElementProfileData[0].metadata_information}
          />
        </div>
      </HomeContent>
    </div>
  )
}

SelectedElementAnalysisPage.displayName = 'SelectedElementAnalysisPage'
export default SelectedElementAnalysisPage
